import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { AuthShell, Field, TextInput, PasswordInput, btnPrimary } from "@/components/AuthShell";

// "Isi cepat email" hanya tampil di mode demo/development.
// Otomatis hilang pada production build (NODE_ENV === "production").
const SHOW_DEMO =
  process.env.REACT_APP_ENABLE_DEMO === "true" || process.env.NODE_ENV !== "production";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const quickFill = (e) => {
    setEmail(e);
    setPassword("");
    toast.info("Email terisi. Masukkan kata sandi Anda.");
  };

  const submit = async (ev) => {
    ev.preventDefault();
    setError("");
    setLoading(true);
    try {
      const user = await login(email.trim(), password);
      toast.success("Berhasil masuk.");
      navigate(user.must_change_password ? "/change-initial-password" : "/dashboard", { replace: true });
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell
      title="Masuk ke Akun"
      subtitle="Silakan masukkan email dan kata sandi Anda untuk mengakses CRM IEC Mustikajaya."
    >
      <form onSubmit={submit} className="space-y-4" data-testid="login-form">
        <Field label="Alamat Email">
          <TextInput
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="contoh: admin.mustikajaya@iec.ac.id"
            data-testid="login-email-input"
          />
        </Field>
        <Field label="Kata Sandi">
          <PasswordInput
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Masukkan kata sandi"
            testId="login-password-input"
          />
        </Field>

        {error && (
          <div
            data-testid="login-error"
            className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2"
          >
            {error}
          </div>
        )}

        <div className="flex justify-end">
          <Link
            to="/forgot-password"
            className="text-sm text-[#1e5ea5] hover:text-[#0f2847] font-medium"
            data-testid="login-forgot-password-link"
          >
            Lupa Kata Sandi?
          </Link>
        </div>

        <button type="submit" disabled={loading} className={btnPrimary} data-testid="login-submit-button">
          {loading && <Loader2 className="h-4 w-4 animate-spin" />}
          Masuk Sekarang
        </button>
      </form>

      {SHOW_DEMO && (
      <div className="mt-6 rounded-xl border border-slate-200 bg-slate-50 p-4" data-testid="login-quickfill-card">
        <p className="text-xs font-semibold text-slate-600 mb-2">Isi cepat email (mode demo/dev — sembunyi di production):</p>
        <div className="flex flex-col gap-2">
          <button
            type="button"
            onClick={() => quickFill("emergentproject36@gmail.com")}
            className="text-left text-xs px-3 py-2 rounded-lg border border-amber-300 bg-amber-50 text-amber-800 hover:bg-amber-100"
            data-testid="quickfill-kepala-button"
          >
            Kepala Cabang · emergentproject36@gmail.com
          </button>
          <button
            type="button"
            onClick={() => quickFill("emergentproject36+admin@gmail.com")}
            className="text-left text-xs px-3 py-2 rounded-lg border border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-100"
            data-testid="quickfill-admin-button"
          >
            Admin Cabang · emergentproject36+admin@gmail.com
          </button>
        </div>
      </div>
      )}
    </AuthShell>
  );
}
